import React, { useEffect, useState } from 'react';
import { FiExternalLink, FiX } from 'react-icons/fi';
import Badge from './Badge';
import { FAILURE_CATEGORY_LABELS, fmtDateTime, fmtDuration, fmtNumber, fmtPct } from './format';
import SmsOutreachService from '../../services/SmsOutreachService';

function Stat({ label, value, sub, tone }) {
  return (
    <div className="rounded-xl border border-zinc-100 bg-zinc-50/60 px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{label}</p>
      <p className={`mt-0.5 text-[17px] font-semibold tabular-nums leading-none ${tone || 'text-zinc-900'}`}>{value}</p>
      {sub ? <p className="mt-1 text-[10.5px] text-zinc-500">{sub}</p> : null}
    </div>
  );
}

/**
 * Bottom sheet with everything recorded for one pipeline run: funnel, outcome,
 * threshold decision, template/lead-day split, row-level failures and a link
 * to the Airflow task log.
 */
export default function RunDetailSheet({ runId, airflowUiBase, onClose }) {
  const [run, setRun] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [log, setLog] = useState(null);
  const [logLoading, setLogLoading] = useState(false);

  useEffect(() => {
    if (!runId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    setLog(null);
    SmsOutreachService.run(runId)
      .then((r) => !cancelled && setRun(r))
      .catch((e) => !cancelled && setError(e.response?.data?.error || e.message || 'Failed to load run'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [runId]);

  useEffect(() => {
    if (!runId) return undefined;
    const onKey = (event) => event.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [runId, onClose]);

  if (!runId) return null;

  const loadLog = async () => {
    if (!run?.airflowRunId) return;
    setLogLoading(true);
    try {
      setLog(await SmsOutreachService.airflowTaskLog(run.airflowRunId, 'run_daily_sms_pipeline', 1));
    } catch (e) {
      setLog({ content: e.response?.data?.error || e.message || 'Log unavailable', truncated: false });
    } finally {
      setLogLoading(false);
    }
  };

  const f = run?.funnel || {};
  const s = run?.summary || {};
  const failRate = s.processed ? (s.failed / s.processed) * 100 : 0;

  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-zinc-900/30 backdrop-blur-[1px]" onClick={onClose} aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Pipeline run details"
        className="absolute inset-x-0 bottom-0 mx-auto flex h-[78vh] max-w-[1400px] flex-col overflow-hidden rounded-t-2xl border border-zinc-200 bg-white shadow-2xl"
      >
        <div className="flex items-center justify-between border-b border-zinc-100 px-5 py-3">
          <div className="flex min-w-0 items-center gap-3">
            <span className="h-1 w-10 rounded-full bg-zinc-200" aria-hidden />
            <div className="min-w-0">
              <h3 className="truncate text-sm font-semibold text-zinc-900">
                Run <span className="font-mono text-[12px] text-zinc-500">{runId}</span>
              </h3>
              {run ? (
                <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                  <Badge state={run.status}>{run.status}</Badge>
                  <Badge tone={run.mode === 'live' ? 'rose' : 'sky'}>{run.mode === 'live' ? 'LIVE' : 'dry run'}</Badge>
                  <Badge tone="zinc">{run.trigger}</Badge>
                  <span className="text-[11px] text-zinc-500">
                    {fmtDateTime(run.startedAt)} PT · {fmtDuration(run.durationMs)} · window {run.window?.start} → {run.window?.end}
                    {run.triggeredBy ? ` · by ${String(run.triggeredBy).replace(/^admin:/, '')}` : ''}
                  </span>
                </div>
              ) : null}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {run?.airflowRunId && airflowUiBase ? (
              <a
                href={`${airflowUiBase}/dags/guest_sms_daily/grid?dag_run_id=${encodeURIComponent(run.airflowRunId)}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-7 items-center gap-1 rounded-md border border-zinc-200 px-2 text-[11px] font-medium text-zinc-700 hover:bg-zinc-50"
              >
                Airflow <FiExternalLink className="h-3 w-3" />
              </a>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
            >
              <FiX className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="grid grid-cols-2 gap-2 laptop:grid-cols-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-16 animate-pulse rounded-xl bg-zinc-50" />
              ))}
            </div>
          ) : error ? (
            <p className="rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700">{error}</p>
          ) : run ? (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-2 laptop:grid-cols-3 desktop:grid-cols-6">
                <Stat label="Exported" value={fmtNumber(f.exported || 0)} sub="reservations in window" />
                <Stat label="Cleaned" value={fmtNumber(f.cleaned || 0)} sub={`${fmtNumber(f.droppedInPreprocess || 0)} dropped`} />
                <Stat label="Ready" value={fmtNumber(f.ready ?? f.selectedRows ?? 0)} sub={`${fmtNumber(f.excludedAlreadyTexted || 0)} already texted`} />
                <Stat label="Sent" value={fmtNumber(s.sent || 0)} tone="text-indigo-700" sub={`${fmtNumber(s.skipped || 0)} skipped`} />
                <Stat
                  label="Failed"
                  value={fmtNumber(s.failed || 0)}
                  tone={s.failed ? 'text-rose-700' : 'text-zinc-900'}
                  sub={s.processed ? `${fmtPct(failRate)} of ${fmtNumber(s.processed)}` : '—'}
                />
                <Stat
                  label="Threshold"
                  value={run.exitCode === 0 || run.exitCode == null ? 'OK' : `exit ${run.exitCode}`}
                  tone={run.exitCode ? 'text-rose-700' : 'text-emerald-700'}
                  sub={run.threshold ? `fail if >${run.threshold.minFailures} and >${run.threshold.maxFailureRatePct}%` : '—'}
                />
              </div>

              <div className="grid gap-4 laptop:grid-cols-3">
                <section className="rounded-xl border border-zinc-100 p-3">
                  <h4 className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Templates</h4>
                  {run.templateBreakdown?.length ? (
                    <ul className="mt-2 space-y-1.5 text-[12px]">
                      {run.templateBreakdown.map((t) => (
                        <li key={t.templateKey} className="flex items-center justify-between">
                          <span className="truncate text-zinc-700">{t.templateKey}</span>
                          <span className="tabular-nums font-medium text-zinc-900">{fmtNumber(t.count)}</span>
                        </li>
                      ))}
                    </ul>
                  ) : run.templateCounts && Object.keys(run.templateCounts).length ? (
                    <ul className="mt-2 space-y-1.5 text-[12px]">
                      {Object.entries(run.templateCounts).map(([k, v]) => (
                        <li key={k} className="flex items-center justify-between">
                          <span className="truncate text-zinc-700">{k}</span>
                          <span className="tabular-nums font-medium text-zinc-900">{fmtNumber(v)}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-2 text-xs text-zinc-400">No sends.</p>
                  )}
                </section>

                <section className="rounded-xl border border-zinc-100 p-3">
                  <h4 className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Lead time</h4>
                  {run.leadDayBreakdown?.length ? (
                    <ul className="mt-2 space-y-1.5 text-[12px]">
                      {run.leadDayBreakdown.map((t) => (
                        <li key={String(t.daysUntilArrival)} className="flex items-center justify-between">
                          <span className="text-zinc-700">{t.daysUntilArrival == null ? 'unknown' : `${t.daysUntilArrival} days before arrival`}</span>
                          <span className="tabular-nums font-medium text-zinc-900">{fmtNumber(t.count)}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-2 text-xs text-zinc-400">No sends.</p>
                  )}
                </section>

                <section className="rounded-xl border border-zinc-100 p-3">
                  <h4 className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Failure categories</h4>
                  {run.failureCategories && Object.keys(run.failureCategories).length ? (
                    <ul className="mt-2 space-y-1.5 text-[12px]">
                      {Object.entries(run.failureCategories)
                        .sort((a, b) => b[1] - a[1])
                        .map(([k, v]) => (
                          <li key={k} className="flex items-center justify-between">
                            <span className="text-zinc-700">{FAILURE_CATEGORY_LABELS[k] || k}</span>
                            <span className="tabular-nums font-medium text-rose-700">{fmtNumber(v)}</span>
                          </li>
                        ))}
                    </ul>
                  ) : (
                    <p className="mt-2 text-xs text-zinc-400">No failures.</p>
                  )}
                </section>
              </div>

              {run.failures?.length ? (
                <section>
                  <div className="mb-1.5 flex items-center justify-between">
                    <h4 className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                      Failed rows {run.failuresTruncated ? '(first 200)' : ''}
                    </h4>
                  </div>
                  <div className="max-h-64 overflow-auto rounded-lg border border-zinc-200">
                    <table className="min-w-full text-left text-xs">
                      <thead className="sticky top-0 bg-zinc-50 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                        <tr>
                          <th className="px-3 py-2">Row</th>
                          <th className="px-3 py-2">Phone</th>
                          <th className="px-3 py-2">CID</th>
                          <th className="px-3 py-2">Check-in</th>
                          <th className="px-3 py-2">Category</th>
                          <th className="px-3 py-2">Reason</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-100 bg-white">
                        {run.failures.map((x, i) => (
                          <tr key={`${x.row}-${i}`}>
                            <td className="px-3 py-1.5 tabular-nums text-zinc-500">{x.row}</td>
                            <td className="px-3 py-1.5 font-mono text-zinc-700">{x.phoneMasked || '—'}</td>
                            <td className="px-3 py-1.5 font-mono text-zinc-700">{x.confirmationId || '—'}</td>
                            <td className="px-3 py-1.5 text-zinc-700">{x.checkInDate || '—'}</td>
                            <td className="px-3 py-1.5">
                              <Badge tone="rose">{FAILURE_CATEGORY_LABELS[x.category] || x.category}</Badge>
                            </td>
                            <td className="max-w-[420px] truncate px-3 py-1.5 text-zinc-600" title={x.reason}>
                              {x.reason}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              ) : null}

              <section>
                <div className="mb-1.5 flex items-center justify-between">
                  <h4 className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Pipeline log</h4>
                  <div className="flex items-center gap-2 text-[11px] text-zinc-500">
                    {run.logFile ? <span className="font-mono">{run.logFile.split('/').slice(-2).join('/')}</span> : null}
                    {run.airflowRunId ? (
                      <button
                        type="button"
                        onClick={loadLog}
                        disabled={logLoading}
                        className="rounded-md border border-zinc-200 px-2 py-0.5 font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
                      >
                        {logLoading ? 'Loading…' : log ? 'Reload Airflow log' : 'Load Airflow log'}
                      </button>
                    ) : null}
                  </div>
                </div>
                {log ? (
                  <pre className="max-h-72 overflow-auto rounded-lg bg-zinc-950 p-3 font-mono text-[10.5px] leading-relaxed text-zinc-200">
                    {log.truncated ? '… (truncated)\n' : ''}
                    {log.content}
                  </pre>
                ) : (
                  <p className="text-xs text-zinc-400">
                    {run.airflowRunId ? 'Tail of the Airflow task log for this run.' : 'This run was started from the CLI; see the log file on the server.'}
                  </p>
                )}
              </section>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
